/**
 * Local draft of the ACTIVE Quick flow (`/app/wycena` → QuickPhotoEstimate).
 *
 * Not to be confused with the legacy `quickEstimateDraft.ts` (old wizard draft,
 * key `prowycena_quick_estimate_draft`), which this module neither reads nor writes.
 *
 * Contract:
 * - v2 drafts live under one key per owner (Firebase uid or `guest`), so a draft
 *   written by one account is never offered to another account on the same device.
 * - A logged-in user may adopt the guest draft of this device (guest → login flow);
 *   a user's draft is never offered to a guest or to another user.
 * - The legacy v1 draft (`prowycena.quickEstimateDraft.v1`, form only, no owner)
 *   still opens, with no manual items and no Margin Shield decisions.
 * - Only the form subset chosen by the caller, the contractor's own manual items
 *   (recomputed through the domain factory) and Margin Shield decisions are stored.
 *   No photos, no calculated result, no contractor identity or contact data.
 * - A present but malformed v2 draft is ignored as a whole instead of being repaired.
 */
import type { MarginShieldDecisions, MarginShieldDecisionStatus } from './approvedOffer';
import {
  createManualEstimateItem,
  MANUAL_ITEM_TYPES,
  MANUAL_ITEM_UNITS,
  validateManualItemInput,
  type ManualEstimateItem,
  type ManualItemInput,
} from './manualEstimateItems';

export const QUICK_ACTIVE_DRAFT_LEGACY_KEY = 'prowycena.quickEstimateDraft.v1';
export const QUICK_ACTIVE_DRAFT_KEY_PREFIX = 'prowycena.quickEstimateDraft.v2:';
export const QUICK_ACTIVE_DRAFT_GUEST_OWNER = 'guest';
const QUICK_ACTIVE_DRAFT_VERSION = 2;
const MARGIN_SHIELD_DECISION_STATUSES: readonly MarginShieldDecisionStatus[] = [
  'suggested',
  'added',
  'skipped',
  'checked',
];

export type QuickDraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Stored shape of a manual item: inputs only, totals are always recomputed. */
interface StoredManualItem {
  id: string;
  name: string;
  quantity: number;
  unit: ManualItemInput['unit'];
  unitPrice: number;
  itemType: ManualItemInput['itemType'];
}

interface QuickActiveDraftPayload {
  version: typeof QUICK_ACTIVE_DRAFT_VERSION;
  owner: string;
  savedAt: string;
  form: Record<string, unknown>;
  manualItems: StoredManualItem[];
  marginShieldDecisions: MarginShieldDecisions;
  decisionScopeKey: string | null;
}

export interface QuickActiveDraftState<TFormPatch> {
  form: TFormPatch;
  manualItems: ManualEstimateItem[];
  marginShieldDecisions: MarginShieldDecisions;
  /** Scope basis the decisions were made for; see `reconcileMarginShieldDecisions`. */
  decisionScopeKey: string | null;
}

export interface LoadedQuickActiveDraft<TFormPatch> extends QuickActiveDraftState<TFormPatch> {
  /** Storage key the draft came from, or null when nothing usable was found. */
  sourceKey: string | null;
}

export function resolveQuickDraftOwner(userId: string | null | undefined): string {
  const trimmed = typeof userId === 'string' ? userId.trim() : '';
  return trimmed ? `user:${trimmed}` : QUICK_ACTIVE_DRAFT_GUEST_OWNER;
}

export function getQuickActiveDraftKey(owner: string): string {
  return `${QUICK_ACTIVE_DRAFT_KEY_PREFIX}${owner}`;
}

export function getBrowserQuickDraftStorage(): QuickDraftStorage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readJson(storage: QuickDraftStorage, key: string): unknown {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function toStoredManualItem(item: ManualEstimateItem): StoredManualItem {
  return {
    id: item.id,
    name: item.label,
    quantity: item.quantity,
    unit: item.unit as ManualItemInput['unit'],
    unitPrice: item.unitPrice,
    itemType: item.itemType,
  };
}

function restoreManualItems(value: unknown): ManualEstimateItem[] | null {
  if (!Array.isArray(value)) return null;
  const restored: ManualEstimateItem[] = [];
  for (const entry of value) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || !entry.id.trim()) return null;
    if (!(MANUAL_ITEM_UNITS as readonly unknown[]).includes(entry.unit)) return null;
    if (!(MANUAL_ITEM_TYPES as readonly unknown[]).includes(entry.itemType)) return null;
    if (typeof entry.name !== 'string' || typeof entry.quantity !== 'number' || typeof entry.unitPrice !== 'number') {
      return null;
    }
    const input: ManualItemInput = {
      name: entry.name,
      quantity: entry.quantity,
      unit: entry.unit as ManualItemInput['unit'],
      unitPrice: entry.unitPrice,
      itemType: entry.itemType as ManualItemInput['itemType'],
    };
    if (validateManualItemInput(input)) return null;
    restored.push(createManualEstimateItem(entry.id, input));
  }
  return restored;
}

function restoreDecisions(value: unknown): MarginShieldDecisions | null {
  if (!isRecord(value)) return null;
  const decisions: MarginShieldDecisions = {};
  for (const [alertId, status] of Object.entries(value)) {
    if (!alertId.trim() || !MARGIN_SHIELD_DECISION_STATUSES.includes(status as MarginShieldDecisionStatus)) {
      return null;
    }
    decisions[alertId] = status as MarginShieldDecisionStatus;
  }
  return decisions;
}

function parseV2Draft<TFormPatch>(
  value: unknown,
  expectedOwner: string,
  sanitizeForm: (form: unknown) => TFormPatch
): QuickActiveDraftState<TFormPatch> | null {
  if (!isRecord(value) || value.version !== QUICK_ACTIVE_DRAFT_VERSION || value.owner !== expectedOwner) return null;
  if (!isRecord(value.form)) return null;
  const manualItems = restoreManualItems(value.manualItems);
  const marginShieldDecisions = restoreDecisions(value.marginShieldDecisions);
  if (!manualItems || !marginShieldDecisions) return null;
  const rawScopeKey = value.decisionScopeKey;
  if (rawScopeKey !== null && typeof rawScopeKey !== 'string') return null;
  const decisionScopeKey: string | null = typeof rawScopeKey === 'string' ? rawScopeKey : null;
  return { form: sanitizeForm(value.form), manualItems, marginShieldDecisions, decisionScopeKey };
}

/**
 * Load order: own v2 draft → (logged-in only) this device's guest v2 draft →
 * legacy v1 form-only draft → nothing. Pure read, no storage writes.
 */
export function loadQuickActiveDraft<TFormPatch>(
  storage: QuickDraftStorage | undefined,
  owner: string,
  sanitizeForm: (form: unknown) => TFormPatch
): LoadedQuickActiveDraft<TFormPatch> {
  const empty: LoadedQuickActiveDraft<TFormPatch> = {
    form: sanitizeForm(null),
    manualItems: [],
    marginShieldDecisions: {},
    decisionScopeKey: null,
    sourceKey: null,
  };
  if (!storage) return empty;

  const ownKey = getQuickActiveDraftKey(owner);
  const own = parseV2Draft(readJson(storage, ownKey), owner, sanitizeForm);
  if (own) return { ...own, sourceKey: ownKey };

  if (owner !== QUICK_ACTIVE_DRAFT_GUEST_OWNER) {
    const guestKey = getQuickActiveDraftKey(QUICK_ACTIVE_DRAFT_GUEST_OWNER);
    const guest = parseV2Draft(readJson(storage, guestKey), QUICK_ACTIVE_DRAFT_GUEST_OWNER, sanitizeForm);
    if (guest) return { ...guest, sourceKey: guestKey };
  }

  const legacy = readJson(storage, QUICK_ACTIVE_DRAFT_LEGACY_KEY);
  if (isRecord(legacy) && isRecord(legacy.form)) {
    return { ...empty, form: sanitizeForm(legacy.form), sourceKey: QUICK_ACTIVE_DRAFT_LEGACY_KEY };
  }

  return empty;
}

/**
 * Writes the owner's draft. A draft adopted from another key (guest or legacy v1)
 * is removed after the first successful write, so it is not offered again.
 */
export function saveQuickActiveDraft(
  storage: QuickDraftStorage | undefined,
  owner: string,
  state: QuickActiveDraftState<Record<string, unknown>>,
  adoptedFromKey?: string | null
): boolean {
  if (!storage) return false;
  const ownKey = getQuickActiveDraftKey(owner);
  const payload: QuickActiveDraftPayload = {
    version: QUICK_ACTIVE_DRAFT_VERSION,
    owner,
    savedAt: new Date().toISOString(),
    form: state.form,
    manualItems: state.manualItems.map(toStoredManualItem),
    marginShieldDecisions: { ...state.marginShieldDecisions },
    decisionScopeKey: state.decisionScopeKey,
  };

  try {
    storage.setItem(ownKey, JSON.stringify(payload));
    if (adoptedFromKey && adoptedFromKey !== ownKey) storage.removeItem(adoptedFromKey);
    return true;
  } catch {
    return false;
  }
}

/** Explicit "Nowa wycena": forget the owner's draft (and the legacy v1 one). */
export function clearQuickActiveDraft(storage: QuickDraftStorage | undefined, owner: string): void {
  if (!storage) return;
  try {
    storage.removeItem(getQuickActiveDraftKey(owner));
    storage.removeItem(QUICK_ACTIVE_DRAFT_LEGACY_KEY);
  } catch {
    // Storage may be unavailable (private mode); the in-memory reset still applies.
  }
}

export interface MarginShieldDecisionScopeInput {
  manualScope: string;
  customScope: string;
  surfaceMode: string;
  pavingMode?: 'surface' | 'edges_only';
  pavingArea?: string;
  pavingLength?: string;
  pavingWidth?: string;
  curbLength?: string;
}

/**
 * The scope a Margin Shield decision was made for. A decision stays valid while the
 * kind of work (`manualScope`), the contractor's scope description (`customScope`,
 * whitespace-normalised) and the measurement mode (`surfaceMode`) are unchanged.
 * Dimensions, rates, VAT and material settings do not invalidate decisions: an
 * "added" item follows the recalculated quantity of the same suggestion.
 */
export function buildMarginShieldDecisionScopeKey(input: MarginShieldDecisionScopeInput): string {
  const positive = (value: string | undefined): boolean => {
    const number = Number(value?.replace(',', '.'));
    return Number.isFinite(number) && number > 0;
  };
  const hasPavingArea = positive(input.pavingArea) ||
    (positive(input.pavingLength) && positive(input.pavingWidth));
  const edgesOnly = input.pavingMode === 'edges_only' ||
    (input.pavingMode === undefined && !hasPavingArea && positive(input.curbLength));
  return JSON.stringify([
    input.manualScope === 'paving' && edgesOnly ? 'paving_edges' : input.manualScope,
    input.customScope.trim().replace(/\s+/gu, ' '),
    input.surfaceMode,
  ]);
}

/**
 * Decisions after a new calculation: kept when the scope key is unchanged (also
 * after F5 + recalculation of the same data), dropped entirely when the scope
 * changed or is unknown, and pruned to suggestions present in the new audit.
 */
export function reconcileMarginShieldDecisions(input: {
  decisions: MarginShieldDecisions;
  decisionScopeKey: string | null;
  nextScopeKey: string;
  availableAlertIds: readonly string[];
}): MarginShieldDecisions {
  if (input.decisionScopeKey === null || input.decisionScopeKey !== input.nextScopeKey) return {};
  const available = new Set(input.availableAlertIds);
  return Object.fromEntries(
    Object.entries(input.decisions).filter(([alertId]) => available.has(alertId))
  );
}
